import { ensureSupabaseSession, getSupabaseClient, isSupabaseConfigured } from '@/lib/supabase';
import { Character, StatType } from '@/data/characters';
import type { BattleFormat, GameSettings } from '@/types/game';

export interface Match {
  id: string;
  room_code: string;
  host_user_id: string | null;
  guest_user_id: string | null;
  status: 'waiting' | 'auction' | 'battle' | 'finished';
  verse: string;
  starting_coins: number;
  bid_timer: number;
  battle_format: BattleFormat;
  auction_pool_ids: string[] | null;
  current_auction_index: number;
  current_stat: StatType | null;
  current_battle_round: number;
  hidden_stat: boolean | null;
  auction_timer_started_at: string | null;
  rebid_mode: boolean | null;
  coin_rewards: boolean | null;
  coin_reward_amount: number | null;
  // Battle roles the host picked; null means all of the verse's roles.
  battle_roles?: string[] | null;
  state_version?: number;
  winner?: 'host' | 'guest' | 'draw' | null;
  created_at: string;
  updated_at: string;
}

export interface MatchPlayer {
  id: string;
  match_id: string;
  user_id: string;
  player_role: 'host' | 'guest';
  display_name: string;
  coins_remaining: number;
  team_ids: string[] | null;
  rounds_won: number;
  current_bid: number;
  bid_locked: boolean;
  selected_character_id: string | null;
  selection_locked: boolean;
  boost_reroll_used?: boolean;
  boost_double_win_used?: boolean;
  boost_double_win_armed?: boolean;
  boost_extra_time_used?: boolean;
  boost_stat_boost_used?: boolean;
  boost_stat_boost_pct?: number;
  boost_weaken_used?: boolean;
  boost_weaken_pct?: number;
  minimum_bid?: number;
  bid_version?: number;
  created_at: string;
  updated_at: string;
}

export interface AuctionRecord {
  id: string;
  match_id: string;
  auction_number: number;
  character_id: string;
  character_data: Character;
  host_bid: number;
  guest_bid: number;
  winner: 'host' | 'guest' | 'tie';
  created_at: string;
}

export interface RoundRecord {
  id: string;
  match_id: string;
  round_number: number;
  stat_type: StatType;
  host_character_id: string | null;
  host_character_data: Character | null;
  host_stat_value: number;
  guest_character_id: string | null;
  guest_character_data: Character | null;
  guest_stat_value: number;
  winner: 'host' | 'guest' | 'tie';
  created_at: string;
}

export interface SpectatorSession {
  spectator_id: string;
  match_status: string;
}

export type ApiResult = { ok: true } | { ok: false; error: string };

function ok(): ApiResult {
  return { ok: true };
}

function fail(message: string, error?: unknown): ApiResult {
  console.error(message, error);
  return { ok: false, error: message };
}

function logSupabaseError(label: string, error: unknown) {
  if (!error || typeof error !== 'object') {
    console.error(label, error);
    return;
  }
  const err = error as {
    message?: string;
    details?: string | null;
    hint?: string | null;
    code?: string | null;
  };
  console.error(label, {
    message: err.message ?? 'unknown',
    details: err.details ?? null,
    hint: err.hint ?? null,
    code: err.code ?? null,
  });
}

let serverOffsetPromise: Promise<number> | null = null;

export async function getServerTimeOffset(): Promise<number> {
  if (serverOffsetPromise) return serverOffsetPromise;
  serverOffsetPromise = (async () => {
    const supabase = await ensureSupabaseSession();
    if (!supabase) return 0;
    const { data, error } = await supabase.rpc('rpc_get_server_time');
    if (error || !data) {
      logSupabaseError('Error fetching server time', error);
      return 0;
    }
    const serverMs = Date.parse(String(data));
    if (!Number.isFinite(serverMs)) return 0;
    return serverMs - Date.now();
  })();
  return serverOffsetPromise;
}

// Generate a random 6-character room code
export function generateRoomCode(): string {
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const bytes = new Uint8Array(6);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => chars[b % chars.length]).join('');
  }
  return Math.random().toString(36).substring(2, 8).toUpperCase();
}

function getClientOrWarn() {
  const client = getSupabaseClient();
  if (!client) {
    console.warn('Supabase not configured');
  }
  return client;
}

// Fire-and-forget cleanup of abandoned matches (>2h old) and expired idempotency keys.
// Called opportunistically when creating or joining a match — no cron job needed.
async function cleanupStaleMatches() {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return;
  supabase.rpc('cleanup_stale_matches').then(({ error }) => {
    if (error) console.warn('Stale match cleanup failed (non-critical):', error.message);
  });
}

// Create a new match
export async function createMatch(
  hostName: string,
  settings: GameSettings,
  auctionPool: Character[]
): Promise<{ match: Match; playerId: string } | null> {
  if (!isSupabaseConfigured()) {
    console.warn('Supabase not configured');
    return null;
  }

  const supabase = await ensureSupabaseSession();
  if (!supabase) return null;

  // Sweep abandoned matches in the background (non-blocking)
  void cleanupStaleMatches();

  const auctionPoolIds = auctionPool.map((character) => character.id);
  
  // Generate idempotency key to prevent duplicate match creation
  const idempotencyKey = `create_match_${Date.now()}_${Math.random().toString(36).substring(7)}`;
  
  const { data, error } = await supabase.rpc('rpc_create_match', {
    p_display_name: hostName,
    p_verse: settings.verse,
    p_starting_coins: settings.startingCoins,
    p_bid_timer: settings.bidTimer,
    p_battle_format: settings.battleFormat,
    p_auction_pool_ids: auctionPoolIds,
    p_hidden_stat: settings.hiddenStat,
    p_rebid_mode: settings.rebidMode,
    p_coin_rewards_enabled: settings.coinRewards,
    p_coin_reward_amount: settings.coinRewardAmount,
    p_idempotency_key: idempotencyKey,
  });

  if (error || !data) {
    logSupabaseError('rpc_create_match error', error);
    console.error('Error creating match');
    return null;
  }

  // The new function returns a JSONB object
  const result = data as { ok: boolean; matchId: string; playerId: string; roomCode: string };
  
  if (!result.ok || !result.matchId || !result.playerId || !result.roomCode) {
    console.error('Invalid response from rpc_create_match');
    return null;
  }

  const { data: match, error: matchError } = await supabase
    .from('matches')
    .select()
    .eq('id', result.matchId)
    .single();

  if (matchError || !match) {
    logSupabaseError('Error fetching match after create', matchError);
    return null;
  }

  return { match: match as Match, playerId: result.playerId };
}

// Join an existing match
type JoinResult =
  | { ok: true; match: Match; playerId: string }
  | { ok: false; error: string };

export async function joinMatch(
  roomCode: string,
  guestName: string
): Promise<JoinResult | null> {
  if (!isSupabaseConfigured()) return null;
  const supabase = await ensureSupabaseSession();
  if (!supabase) return null;

  // Sweep abandoned matches in the background (non-blocking)
  void cleanupStaleMatches();

  const normalizedRoomCode = roomCode.trim().toUpperCase();

  // Pre-check: look up the room code so we can give a specific error
  const { data: existing } = await supabase
    .from('matches')
    .select('id, status, guest_user_id')
    .eq('room_code', normalizedRoomCode)
    .maybeSingle();

  if (!existing) {
    return { ok: false, error: 'Room not found. Check the code and try again.' };
  }
  if (existing.status !== 'waiting') {
    return { ok: false, error: 'This match has already started.' };
  }
  if (existing.guest_user_id) {
    return { ok: false, error: 'This room is already full.' };
  }

  const { data, error } = await supabase.rpc('rpc_join_match', {
    p_room_code: normalizedRoomCode,
    p_display_name: guestName,
  });

  if (error || !data?.length) {
    logSupabaseError('rpc_join_match error', error);
    const msg = (error as { message?: string })?.message ?? '';
    if (msg.includes('already has a guest')) {
      return { ok: false, error: 'This room is already full.' };
    }
    return { ok: false, error: 'Failed to join match. Try again.' };
  }

  const matchId = data[0].match_id as string;
  const playerId = data[0].player_id as string;

  const { data: match, error: matchError } = await supabase
    .from('matches')
    .select()
    .eq('id', matchId)
    .single();

  if (matchError || !match) {
    logSupabaseError('Error fetching match after join', matchError);
    return null;
  }

  return { ok: true, match: match as Match, playerId };
}

export async function updateDisplayName(
  matchId: string,
  displayName: string,
  _playerId?: string | null
): Promise<ApiResult> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return fail('Supabase not configured');

  const { error } = await supabase.rpc('rpc_update_display_name', {
    p_match_id: matchId,
    p_display_name: displayName,
  });

  if (error) {
    return fail('Error updating display name', error);
  }
  return ok();
}

// Update match status — if transitioning to 'finished', delete the match entirely
export async function updateMatchStatus(
  matchId: string,
  status: Match['status']
): Promise<ApiResult> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return fail('Supabase not configured');

  if (status === 'auction') {
    const { error } = await supabase.rpc('rpc_start_auction_match', {
      p_match_id: matchId,
    });
    if (error) return fail('Error starting match', error);
    return ok();
  }

  if (status === 'finished') {
    // Don't persist finished matches — just delete
    const { error } = await supabase.from('matches').delete().eq('id', matchId);
    if (error) return fail('Error deleting finished match', error);
    return ok();
  }

  const { error } = await supabase
    .from('matches')
    .update({
      status,
      auction_timer_started_at: null,
      updated_at: new Date().toISOString(),
    })
    .eq('id', matchId);
  if (error) return fail('Error updating match status', error);
  return ok();
}

export async function startAuctionTimer(matchId: string): Promise<ApiResult> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return fail('Supabase not configured');

  const { error } = await supabase.rpc('rpc_start_auction_timer', {
    p_match_id: matchId,
  });

  if (error) return fail('Error starting auction timer', error);
  return ok();
}

export async function rebid(matchId: string, bid: number): Promise<ApiResult> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return fail('Supabase not configured');

  const { error } = await supabase.rpc('rpc_rebid', {
    p_match_id: matchId,
    p_bid: bid,
  });

  if (error) return fail('Error rebid', error);
  return ok();
}

export async function updateMatchSettings(
  matchId: string,
  settings: {
    verse?: string;
    starting_coins?: number;
    bid_timer?: number;
    battle_format?: BattleFormat;
    auction_pool_ids?: string[];
    hidden_stat?: boolean;
    rebid_mode?: boolean;
    coin_rewards_enabled?: boolean;
    coin_reward_amount?: number;
    battle_roles?: string[] | null;
  }
): Promise<ApiResult> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return fail('Supabase not configured');

  const payload = {
    ...settings,
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase
    .from('matches')
    .update(payload)
    .eq('id', matchId);
  if (error) return fail('Error updating match settings', error);
  return ok();
}

export async function forceLockBids(matchId: string): Promise<ApiResult> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return fail('Supabase not configured');

  const { error } = await supabase.rpc('rpc_force_lock_bids', {
    p_match_id: matchId,
  });

  if (error) return fail('Error force locking bids', error);
  return ok();
}

// Update player bid
export async function updatePlayerBid(
  matchId: string,
  bid: number,
  locked: boolean,
  _playerId?: string | null
): Promise<ApiResult> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return fail('Supabase not configured');

  const { error } = await supabase.rpc('rpc_update_bid', {
    p_match_id: matchId,
    p_bid: bid,
    p_locked: locked,
  });

  if (error) return fail('Error updating player bid', error);
  return ok();
}

// Update player selection
export async function updatePlayerSelection(
  matchId: string,
  characterId: string | null,
  locked: boolean,
  _playerId?: string | null
): Promise<ApiResult> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return fail('Supabase not configured');

  const { error } = await supabase.rpc('rpc_update_selection', {
    p_match_id: matchId,
    p_character_id: characterId,
    p_locked: locked,
  });

  if (error) return fail('Error updating player selection', error);
  return ok();
}

export async function resolveAuction(
  matchId: string,
  _auctionNumber?: number,
  _character?: Character,
  _nextStat?: StatType | null,
  expectedStateVersion?: number | null,
  idempotencyKey?: string | null
): Promise<ApiResult & { data?: Record<string, unknown> }> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return fail('Supabase not configured');

  const { data, error } = await supabase.rpc('rpc_resolve_auction_v2', {
    p_match_id: matchId,
    p_expected_state_version: expectedStateVersion ?? null,
    p_idempotency_key: idempotencyKey ?? null,
  });

  if (error) return fail('Auction resolution failed: ' + (error as { message?: string }).message, error);
  return { ok: true, data: data as Record<string, unknown> };
}

export async function resolveBattleRound(
  matchId: string,
  _roundNumber: number,
  _stat: StatType,
  _hostCharacter: Character,
  hostStatValue: number,
  _guestCharacter: Character,
  guestStatValue: number,
  _nextStat?: StatType | null,
  expectedStateVersion?: number | null,
  idempotencyKey?: string | null
): Promise<ApiResult & { data?: Record<string, unknown> }> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return fail('Supabase not configured');

  const { data, error } = await supabase.rpc('rpc_resolve_battle_round_v2', {
    p_match_id: matchId,
    p_host_stat_value: hostStatValue,
    p_guest_stat_value: guestStatValue,
    p_expected_state_version: expectedStateVersion ?? null,
    p_next_stat: _nextStat ?? null,
    p_idempotency_key: idempotencyKey ?? null,
  });

  if (error) return fail('Battle round resolution failed: ' + (error as { message?: string }).message, error);
  return { ok: true, data: data as Record<string, unknown> };
}

export async function purchaseBoost(
  matchId: string,
  boostType: 'reroll' | 'double_win' | 'extra_time' | 'stat_boost' | 'weaken',
  percent?: 5 | 10,
  nextStat?: StatType | null
): Promise<ApiResult> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return fail('Supabase not configured');

  const { error } = await supabase.rpc('rpc_purchase_boost', {
    p_match_id: matchId,
    p_boost_type: boostType,
    p_percent: percent ?? null,
    p_next_stat: nextStat ?? null,
  });

  if (error) return fail('Error purchasing boost', error);
  return ok();
}

export async function forfeitMatch(matchId: string): Promise<ApiResult> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return fail('Supabase not configured');

  const { error } = await supabase.rpc('rpc_forfeit_match', {
    p_match_id: matchId,
  });

  if (error) return fail('Error forfeiting match', error);
  return ok();
}

// Subscribe to match updates
const MATCH_COLUMNS = 'id, room_code, host_user_id, guest_user_id, status, verse, starting_coins, bid_timer, battle_format, auction_pool_ids, current_auction_index, current_stat, current_battle_round, hidden_stat, auction_timer_started_at, rebid_mode, state_version, winner, created_at, updated_at';
let matchHasBattleRoles = true;

export function subscribeToMatch(matchId: string, callback: (match: Match) => void) {
  if (typeof window === 'undefined') return () => {};
  const supabase = getClientOrWarn();
  if (!supabase) return () => {};

  // Clearing a timeout alone does not stop an in-flight request or a delayed
  // channel close callback from starting the polling loop again after cleanup.
  let active = true;
  let debounceId: number | null = null;
  let latestMatch: Match | null = null;
  const emit = (match: Match) => {
    if (!active) return;
    latestMatch = match;
    if (debounceId) {
      window.clearTimeout(debounceId);
    }
    debounceId = window.setTimeout(() => {
      debounceId = null;
      if (active && latestMatch) callback(latestMatch);
    }, 50);
  };

  let pollStopped = false;

  const fetchMatch = async () => {
    if (!active) return;
    const read = (columns: string) => supabase.from('matches').select(columns).eq('id', matchId).maybeSingle();
    let { data, error } = await read(`${MATCH_COLUMNS}${matchHasBattleRoles ? ', battle_roles' : ''}`);
    if (error?.code === '42703' && matchHasBattleRoles) {
      // Database without the battle_roles column yet.
      matchHasBattleRoles = false;
      ({ data, error } = await read(MATCH_COLUMNS));
    }

    if (!active) return;
    if (error) {
      console.error('Error fetching match:', error);
      return;
    }

    if (!data) {
      // Match was deleted — stop polling
      pollStopped = true;
      return;
    }

    // The column list is built at runtime, so the client can't infer the row type.
    emit(data as unknown as Match);
  };

  let pollTimeout: number | null = null;
  let channelHealthy = false;

  const schedulePoll = () => {
    if (!active || pollTimeout || pollStopped) return;
    const interval = channelHealthy
      ? document.hidden
        ? 12000
        : 4000
      : document.hidden
      ? 6000
      : 2000;
    pollTimeout = window.setTimeout(async () => {
      pollTimeout = null;
      if (!active || pollStopped) return;
      await fetchMatch();
      schedulePoll();
    }, interval);
  };

  const stopPoll = () => {
    if (pollTimeout) {
      window.clearTimeout(pollTimeout);
      pollTimeout = null;
    }
  };

  void fetchMatch();
  schedulePoll();

  const channel = supabase
    .channel(`match:${matchId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'matches',
        filter: `id=eq.${matchId}`,
      },
      (payload) => {
        emit(payload.new as Match);
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'DELETE',
        schema: 'public',
        table: 'matches',
        filter: `id=eq.${matchId}`,
      },
      () => {
        // Match was deleted (game over or forfeit) — synthesise a finished snapshot
        // so the client transitions to the results screen.
        // Pass current_battle_round = -1 as a sentinel so applyMatchSnapshot
        // knows this is a delete event (not a genuine round 0 = no-team-loss)
        const syntheticFinished = { id: matchId, status: 'finished', current_battle_round: -1 } as Match;
        emit(syntheticFinished);
      }
    )
    // Egress: the database now broadcasts only the fields the client uses (migration
    // 20261103120000) instead of streaming the whole row; the events above cover older databases.
    .on('broadcast', { event: 'match_changed' }, ({ payload }) => {
      emit(payload as Match);
    })
    .on('broadcast', { event: 'match_deleted' }, () => {
      emit({ id: matchId, status: 'finished', current_battle_round: -1 } as Match);
    })
    .subscribe((status) => {
      if (!active) return;
      if (status === 'SUBSCRIBED') {
        channelHealthy = true;
        stopPoll();
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        channelHealthy = false;
        schedulePoll();
      }
    });

  const handleVisibility = () => {
    if (!channelHealthy) {
      stopPoll();
      schedulePoll();
    }
  };
  document.addEventListener('visibilitychange', handleVisibility);

  return () => {
    active = false;
    supabase.removeChannel(channel);
    stopPoll();
    if (debounceId) window.clearTimeout(debounceId);
    document.removeEventListener('visibilitychange', handleVisibility);
  };
}

// Subscribe to player updates
export function subscribeToPlayers(matchId: string, callback: (players: MatchPlayer[]) => void) {
  if (typeof window === 'undefined') return () => {};
  const supabase = getClientOrWarn();
  if (!supabase) return () => {};

  let active = true;
  let debounceId: number | null = null;
  let latestPlayers: MatchPlayer[] | null = null;
  const emit = (players: MatchPlayer[]) => {
    if (!active) return;
    latestPlayers = players;
    if (debounceId) {
      window.clearTimeout(debounceId);
    }
    debounceId = window.setTimeout(() => {
      debounceId = null;
      if (active && latestPlayers) callback(latestPlayers);
    }, 50);
  };

  // Sequence counter — ensures that if two fetchPlayers() calls are in-flight
  // simultaneously, only the result from the LATEST call is applied. Without
  // this, a slow query started BEFORE a round resolves can complete AFTER a
  // faster query started post-resolution, delivering stale selection_locked=true
  // data that re-triggers the old-battle flash and loops the comparison.
  let fetchSeq = 0;

  // Egress: the lite read leaves out each player's full team objects, which the client never uses
  // (it works from team_ids). Fall back to the safe read until migration 20261103120000 is applied.
  let liteMissing = false;

  const fetchPlayers = async () => {
    if (!active) return;
    const mySeq = ++fetchSeq;
    let data: unknown = null;
    let error: { code?: string; message?: string } | null = null;
    if (!liteMissing) {
      ({ data, error } = await supabase.rpc('rpc_get_match_players_lite', { p_match_id: matchId }));
      if (error?.code === 'PGRST202') liteMissing = true;
    }
    if (liteMissing) {
      ({ data, error } = await supabase.rpc('rpc_get_match_players_safe', {
        p_match_id: matchId,
      }));
    }
    if (!active) return;
    if (error?.code === 'PGRST202') {
      // A Pages deploy can precede its database migration. Keep existing
      // rooms working until the safe read RPC and table revokes are applied.
      ({ data, error } = await supabase.from('match_players').select('*').eq('match_id', matchId));
    }

    if (!active) return;
    if (error) {
      console.error('Error fetching players:', error);
      return;
    }

    // Discard if a newer fetch has already completed — prevents an older slow
    // query from overwriting fresh data with stale selection state.
    if (data && mySeq === fetchSeq) {
      emit(data as MatchPlayer[]);
    }
  };

  let pollTimeout: number | null = null;
  let channelHealthy = false;
  // Set once the database's player_changed broadcast is seen (migration
  // 20261030120000). Until then polling is the only way to see the opponent's
  // moves, so it stays fast; afterwards it is just a slow safety net.
  let signalSeen = false;

  const schedulePoll = () => {
    if (!active || pollTimeout) return;
    const interval = channelHealthy
      ? signalSeen
        ? document.hidden ? 20000 : 10000
        : document.hidden ? 6000 : 1500
      : document.hidden ? 4000 : 1000;
    pollTimeout = window.setTimeout(async () => {
      pollTimeout = null;
      await fetchPlayers();
      schedulePoll();
    }, interval);
  };

  const stopPoll = () => {
    if (pollTimeout) {
      window.clearTimeout(pollTimeout);
      pollTimeout = null;
    }
  };

  void fetchPlayers();
  schedulePoll();

  const channel = supabase
    .channel(`players:${matchId}`)
    .on('broadcast', { event: 'player_changed' }, () => { signalSeen = true; void fetchPlayers(); })
    .subscribe((status) => {
      if (!active) return;
      if (status === 'SUBSCRIBED') {
        channelHealthy = true;
        stopPoll();
        schedulePoll();
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        channelHealthy = false;
        schedulePoll();
      }
    });

  const handleVisibility = () => {
    if (!channelHealthy) {
      stopPoll();
      schedulePoll();
    }
  };
  document.addEventListener('visibilitychange', handleVisibility);

  return () => {
    active = false;
    supabase.removeChannel(channel);
    stopPoll();
    if (debounceId) window.clearTimeout(debounceId);
    document.removeEventListener('visibilitychange', handleVisibility);
  };
}

// ─── BATTLE ROUNDS SUBSCRIPTION ───────────────────────────────────────────────
// Subscribes to match_rounds inserts so BOTH players always have the same
// server-authoritative battleHistory. This fixes all scorecard desync issues:
// - different characters shown per client
// - tired character not syncing
// - round counts differing
export function subscribeToBattleRounds(
  matchId: string,
  callback: (rounds: RoundRecord[]) => void
) {
  if (typeof window === 'undefined') return () => {};
  const supabase = getClientOrWarn();
  if (!supabase) return () => {};

  let active = true;

  // Sequence counter — same race-condition protection as subscribeToPlayers.
  // A slow fetchRounds() started before round N resolves can complete after a
  // faster one that already has round N, briefly reverting battleHistory to
  // N-1 rounds and showing the wrong winner on the result card.
  let roundsFetchSeq = 0;

  // Egress: each round carries both fighters' full character data, so re-downloading the whole
  // list every few seconds was the biggest cost of a match. Fetch only rounds we don't have yet,
  // with a full reload every 30 s in case rounds were reset.
  let known: RoundRecord[] = [];
  let lastFullFetch = 0;
  const FULL_REFRESH_MS = 30_000;

  const fetchRounds = async () => {
    if (!active) return;
    const mySeq = ++roundsFetchSeq;
    const full = known.length === 0 || Date.now() - lastFullFetch > FULL_REFRESH_MS;
    const lastRound = full ? null : known[known.length - 1].round_number;
    let query = supabase
      .from('match_rounds')
      .select('id, match_id, round_number, stat_type, host_character_id, host_character_data, host_stat_value, guest_character_id, guest_character_data, guest_stat_value, winner, created_at')
      .eq('match_id', matchId);
    if (lastRound !== null) query = query.gt('round_number', lastRound);
    const { data, error } = await query.order('round_number', { ascending: true });

    if (!active) return;
    if (error) {
      console.error('Error fetching battle rounds:', error);
      return;
    }

    if (data && mySeq === roundsFetchSeq) {
      const rows = data as RoundRecord[];
      if (full) {
        lastFullFetch = Date.now();
        known = rows;
      } else if (rows.length === 0) {
        return; // nothing new, nothing to tell the screen
      } else {
        const byId = new Map(known.map((r) => [r.id, r]));
        for (const r of rows) byId.set(r.id, r);
        known = [...byId.values()].sort((a, b) => a.round_number - b.round_number);
      }
      callback(known);
    }
  };

  void fetchRounds();

  // ── POLLING FALLBACK ────────────────────────────────────────────────────
  // If the WebSocket INSERT event is missed (connection blip, tab hidden, etc)
  // the tiredPlayerId would never update, breaking the tired mechanic.
  // Poll every 3 s so we always catch up even without a real-time event.
  let pollTimeout: number | null = null;
  let channelHealthy = false;

  const schedulePoll = () => {
    if (!active || pollTimeout) return;
    // New rounds arrive instantly over the live INSERT event; this poll is only a safety net.
    const interval = channelHealthy
      ? document.hidden ? 20000 : 8000
      : document.hidden ? 5000  : 2000;
    pollTimeout = window.setTimeout(async () => {
      pollTimeout = null;
      await fetchRounds();
      schedulePoll();
    }, interval);
  };

  const stopPoll = () => {
    if (pollTimeout) { window.clearTimeout(pollTimeout); pollTimeout = null; }
  };

  schedulePoll();

  const channel = supabase
    .channel(`rounds:${matchId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'match_rounds',
        filter: `match_id=eq.${matchId}`,
      },
      async () => {
        // Re-fetch all rounds to get the full ordered list
        await fetchRounds();
      }
    )
    // The database now sends just this signal (migration 20261103120000) rather than the full row.
    .on('broadcast', { event: 'round_added' }, () => {
      void fetchRounds();
    })
    .subscribe((status) => {
      if (!active) return;
      if (status === 'SUBSCRIBED') {
        channelHealthy = true;
        stopPoll();
        schedulePoll(); // restart at healthy interval
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        channelHealthy = false;
        schedulePoll();
      }
    });

  return () => {
    active = false;
    supabase.removeChannel(channel);
    stopPoll();
  };
}

// Get match players
export async function getMatchPlayers(matchId: string): Promise<MatchPlayer[]> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return [];

  let { data, error } = await supabase.rpc('rpc_get_match_players_safe', {
    p_match_id: matchId,
  });
  if (error?.code === 'PGRST202') {
    ({ data, error } = await supabase.from('match_players').select('*').eq('match_id', matchId));
  }

  if (error) {
    console.error('Error fetching players:', error);
    return [];
  }

  return data as MatchPlayer[];
}

// Find a match by room code (used for spectate flow)
export async function getMatchByRoomCode(
  roomCode: string
): Promise<{ id: string; room_code: string; status: Match['status'] } | null> {
  if (!isSupabaseConfigured()) return null;
  const supabase = await ensureSupabaseSession();
  if (!supabase) return null;

  const normalizedRoomCode = roomCode.trim().toUpperCase();
  const { data, error } = await supabase.rpc('rpc_lookup_match_by_room_code', {
    p_room_code: normalizedRoomCode,
  });

  if (error || !data?.length) {
    if (error) logSupabaseError('Error fetching match by room code', error);
    return null;
  }

  return {
    id: data[0].match_id as string,
    room_code: data[0].room_code as string,
    status: data[0].status as Match['status'],
  };
}

// ============================================================================
// REALTIME OPTIMIZATION - Instant Updates
// ============================================================================

// ─── QUICK MESSAGES ──────────────────────────────────────────────────────────
// Uses `quick_msg:${matchId}` — NOT `realtime:${matchId}` (which is the bid
// channel). Supabase only allows one subscription per channel name per client;
// sharing the name silently drops one of the two subscriptions.

export interface QuickMessagePayload {
  player_role: 'host' | 'guest';
  message: string;
  timestamp: string;
}

export function subscribeToQuickMessages(
  matchId: string,
  callback: (payload: QuickMessagePayload) => void
) {
  if (typeof window === 'undefined') return () => {};
  const supabase = getClientOrWarn();
  if (!supabase) return () => {};

  const channel = supabase
    .channel(`quick_msg:${matchId}`)
    .on('broadcast', { event: 'quick_message' }, (payload) => {
      callback(payload.payload as QuickMessagePayload);
    })
    .subscribe();

  return () => { supabase.removeChannel(channel); };
}

// Pass the already-subscribed channel as `subscribedChannel` so this function
// can reuse it. Creating a fresh supabase.channel() here doesn't work because
// the new instance hasn't joined the WebSocket yet.
export async function broadcastQuickMessage(
  matchId: string,
  message: string,
  playerRole: 'host' | 'guest',
  subscribedChannel?: { send: (msg: object) => Promise<unknown> } | null
): Promise<void> {
  const payload = {
    type: 'broadcast',
    event: 'quick_message',
    payload: {
      player_role: playerRole,
      message,
      timestamp: new Date().toISOString(),
    } satisfies QuickMessagePayload,
  };

  if (subscribedChannel) {
    await subscribedChannel.send(payload);
    return;
  }

  // Fallback: create, subscribe, send, then tear down
  const supabase = await ensureSupabaseSession();
  if (!supabase) return;
  const ch = supabase.channel(`quick_msg:${matchId}`);
  ch.subscribe();
  await ch.send(payload);
  supabase.removeChannel(ch);
}

// ============================================================================
// SPECTATOR MODE
// ============================================================================

export async function joinAsSpectator(
  matchId: string,
  displayName: string = 'Anonymous'
): Promise<SpectatorSession | null> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return null;

  const { data, error } = await supabase.rpc('rpc_join_as_spectator', {
    p_match_id: matchId,
    p_display_name: displayName,
  });

  if (error || !data?.success) {
    console.error('Error joining as spectator:', error);
    return null;
  }

  return {
    spectator_id: data.spectator_id,
    match_status: data.match_status,
  };
}

export async function leaveAsSpectator(spectatorId: string): Promise<void> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return;

  await supabase.rpc('rpc_leave_as_spectator', {
    p_spectator_id: spectatorId,
  });
}

export async function getSpectatorCount(matchId: string): Promise<number> {
  const supabase = await ensureSupabaseSession();
  if (!supabase) return 0;

  const { data, error } = await supabase.rpc('rpc_get_spectator_count', {
    p_match_id: matchId,
  });

  if (error) return 0;
  return Number(data ?? 0);
}
